<!--@format-->
<template>
    <div :class="['hubspot-send-contact-widget']">
        <div class="widget-header">
            <h3>Send To HubSpot</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div v-if="isInitializing">
            <loading-screen-svg />
        </div>
        <div v-else class="content-wrapper">
            <hubspot-send-contact
                v-if="contact"
                @selectCompany="selectCompany"
                @removeCompany="removeCompany"
                :contact="contact"
                :company="company"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" :onClick="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button
                    :onClick="sendContactToHubSpotPSA"
                    :loading="isSendingContactToHubSpotPSA"
                    :disabled="isSendingContactToHubSpotPSA || deactivatedSwitches"
                >
                    Send to HubSpot
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const HubSpotPSASendContact = () => import('@/components/integration/hubspot-send-contact');

import EventBus from '@/scripts/event-bus.js';
import log, { LogLevel } from "@/scripts/logger";

export default {
    name: 'hubspot-send-contact-widget',

    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'hubspot-send-contact': HubSpotPSASendContact,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            contact: {
                id: null,
                name: null,
                profileImageUrl: null,
                shouldSendInformation: true,
                shouldSendOpportunities: false,
                shouldSendActivities: false,
                errorMessage: null,
                isUpsert: null,
                clientId: null,
                hubSpotContactId: null,
            },

            company: {
                id: null,
                name: null,
                imageUrl: null,
                hubSpotCompanyId: -1,
            },

            isSavingCompanyToContact: false,
            isSendingContactToHubSpotPSA: false,
            deactivatedSwitches: false,
            hubspotContactLinkData: {
                hubspotSite: null,
                hubspotContactId: null,
            },

            isInitializing: true,
        };
    },

    computed: {
        hubSpotLink: function () {
            return `${this.hubspotContactLinkData.hubspotSite}/contact/`;
        },
    },

    watch: {
        'contact.shouldSendInformation': function () {
            this.deactivatedSwitches = !(
                this.contact.shouldSendInformation ||
                this.contact.shouldSendOpportunities ||
                this.contact.shouldSendActivities
            );
        },

        'contact.shouldSendOpportunities': function () {
            this.deactivatedSwitches = !(
                this.contact.shouldSendInformation ||
                this.contact.shouldSendOpportunities ||
                this.contact.shouldSendActivities
            );
        },

        'contact.shouldSendActivities': function () {
            this.deactivatedSwitches = !(
                this.contact.shouldSendInformation ||
                this.contact.shouldSendOpportunities ||
                this.contact.shouldSendActivities
            );
        },
    },

    created() {
        this.getData();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getData() {
            this.getContact()
                .then(this.getIntegrationInformation)
                .catch((error) => {
                    this.$helpers.showFailNotification(error, { error });
                });
        },

        getContact() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts?$filter=Id eq ${this.widget.options.id}&$expand=Company`
                    )
                    .then((response) => {
                        this.createContact(response.data.value[0]);

                        if (response.data.value[0].Company) {
                            this.createCompany(response.data.value[0].Company);
                        }

                        resolve();
                    })
                    .catch((error) => {
                        const message = 'An error occurred while fetching the contacts you selected.';
                        log.error(message, error);
                        reject(message);
                    });
            });
        },

        getIntegrationInformation() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/ms/hubspot-api/v1/credentials/${this.contact.clientId}`)
                    .then((response) => {
                        this.hubspotContactLinkData.hubspotSite = response.data.data.BaseUrl;
                    })
                    .catch((error) => {
                        const message = 'An error occurred while fetching your HubSpot integration info.';
                        log.error(message, error);
                        reject(message);
                    })
                    .finally(() => {
                        this.isInitializing = false;
                    });
            });
        },

        createContact(contactsResponse) {
            this.contact.id = contactsResponse.Id;
            this.contact.name = this.$helpers.getName(
                contactsResponse.FirstName,
                contactsResponse.LastName
            );
            this.contact.profileImageUrl =
                contactsResponse.ProfileImageUrl || require('@/assets/images/avatar.png');
            this.contact.shouldSendInformation = true;
            this.contact.shouldSendOpportunities = false;
            this.contact.shouldSendActivities = false;
            this.contact.errorMessage = null;
            this.contact.selectedOption = contactsResponse.IsUpsert ? 'upsert' : 'insert';
            this.contact.clientId = contactsResponse.ClientId;
        },

        createCompany(company) {
            this.company.id = company.Id;
            this.company.name = company.Name;
            this.company.imageUrl =
                company.LogoUrl || require('@/assets/images/company-avatar.png');
            this.company.hubSpotCompanyId = company.HubSpotCompanyId;
        },

        selectCompany(company) {
            this.company = {
                id: company.id,
                name: company.name,
                imageUrl: company.imageUrl,
            };

            this.saveCompanyToContact();
        },

        removeCompany() {
            this.company = null;
        },

        openCreateCompanyWidget() {
            var widget = {
                name: 'quick-add-company-widget',
            };

            EventBus.$emit('open-widget', widget);
        },

        saveCompanyToContact() {
            this.isSavingCompanyToContact = true;

            var changes = [
                {
                    name: 'CompanyId',
                    value: this.company.id,
                },
            ];

            this.$api
                .patch(`/api/Contacts/${this.widget.options.id}`, changes)
                .then((response) => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showSuccessNotification(
                        `<strong>${this.contact.name}</strong> has been added to ${this.company.name}`
                    );
                })
                .catch((error) => {
                    this.isSavingCompanyToContact = false;
                    this.$helpers.showFailNotification(`Unable to add ${this.contact.name} to ${this.company.name}.`,
                        { level: LogLevel.DEBUG }
                    );
                    log.error('Unable to add contact to company', error);
                });
        },

        sendContactToHubSpotPSA() {
            this.isSendingContactToHubSpotPSA = true;

            this.sendCompanyInfo()
                .then(this.sendContactInfo)
                .then(this.getHubSpotPSAContactId)
                .then(() => {
                    this.isSendingContactToHubSpotPSA = false;
                    this.contact.errorMessage = null;

                    this.createSuccessMessage();

                    EventBus.$emit('contact:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isSendingContactToHubSpotPSA = false;
                    this.contact.errorMessage = error.Message;
                });
        },

        getHubSpotPSAContactId() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Contacts?$filter=Id eq ${this.contact.id} &$select=HubSpotContactId`
                    )
                    .then((response) => {
                        this.hubspotContactLinkData.hubspotContactId =
                            response.data.value[0].HubSpotContactId;
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                    });
            });
        },

        createSuccessMessage() {
            var actionTaken = this.contact.isUpsert ? 'updated' : 'inserted';
            var message = `<strong>${this.contact.name}</strong> has been ${actionTaken} in HubSpot.`;
            let linkContactSite =
                this.hubSpotLink + `${this.hubspotContactLinkData.hubspotContactId}`;

            EventBus.$emit('show-notification', {
                iconUrl: require('@/assets/images/SVG/checkmark.svg'),
                isIconSvg: true,
                message: message,
                actionLabel: 'View',
                link: linkContactSite,
                action: null,
            });
        },

        sendCompanyInfo() {
            return new Promise((resolve, reject) => {
                if (this.company && !this.company.hubSpotCompanyId) {
                    var request = {
                        isUpsert: false,
                    };

                    this.$api
                        .post(
                            `/ms/hubspot-api/v1/integrations/export/companies/${this.company.id}/${this.contact.clientId}`,
                            request
                        )
                        .then((response) => {
                            this.$helpers.showSuccessNotification(
                                `${this.company.name} has been inserted in HubSpot`
                            );
                            resolve();
                        })
                        .catch((response) => {
                            reject(response.response.data);
                        });
                } else {
                    resolve();
                }
            });
        },

        sendContactInfo() {
            return new Promise((resolve, reject) => {
                var request = {
                    isUpsert: this.contact.isUpsert,
                    exportContacts: this.contact.shouldSendInformation,
                    exportDeals: this.contact.shouldSendOpportunities,
                    exportActivities: this.contact.shouldSendActivities,
                };

                this.$api
                    .post(
                        `/ms/hubspot-api/v1/integrations/export/contacts/${this.contact.id}/${this.contact.clientId}`,
                        request
                    )
                    .then((response) => {
                        resolve();
                    })
                    .catch((response) => {
                        reject(response.response.data);
                    });
            });
        },
    },
};
</script>

<style scoped>
.hubspot-send-contact-widget .content-wrapper {
    padding-left: 0px;
    padding-right: 0px;
}

.hubspot-send-contact-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.hubspot-send-contact-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.hubspot-send-contact-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.hubspot-send-contact-widget .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.hubspot-send-contact-widget .company-options-container h4 {
    margin-bottom: 20px;
}

.hubspot-send-contact-widget .widget-content .processing {
    display: block;
    width: 200px;
    margin: 0 auto;
}

.hubspot-send-contact-widget .widget-content .row {
    margin-bottom: 30px;
}
</style>
